import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { Palette } from '../palette/palettes';
import { createBody, type Body } from '../physics/Body';
import { PhysicsWorld } from '../physics/PhysicsWorld';
import { attractionForce } from '../physics/pointerForce';
import { sampleAlignment, softFieldForces } from '../physics/alignment';

const TUNING = {
  attraction: 2200,
  maxForce: 2100,
  interactionRadius: 120,
  innerSoftRadius: 16,
  peakForceDistance: 44,
  torqueStrength: 1.1,
  linearDamping: 0.88,
  angularDamping: 0.5,
  restitution: 0.22,
  maxSpeed: 580,
  maxAngular: 5.5,
  snapDistanceRatio: 0.22,
  snapAngleDeg: 20,
  softFieldRatio: 0.52,
  softAngleDeg: 36,
  softPull: 640,
  softDamp: 3.8,
  ambientPull: 70,
  squareSize: 36,
  receiverScale: 1.2,
  snapDuration: 0.28,
  snapHold: 0.1,
};

/**
 * LinkedIn back gate — drag one square into the hole to return.
 */
export class BackPuzzle {
  private app: Application | null = null;
  private root = new Container();
  private field = new Graphics();
  private receiverGfx = new Graphics();
  private playerGfx = new Graphics();
  private label = new Text({
    text: 'BACK',
    style: new TextStyle({
      fill: 0x121820,
      fontFamily: 'BebasRegular, Impact, sans-serif',
      fontSize: 13,
      letterSpacing: 5,
    }),
  });
  private world = new PhysicsWorld(TUNING);
  private body!: Body;
  private receiver = { x: 0, y: 0, angle: 0, size: 0 };
  private pointer = { x: 0, y: 0, active: false };
  private zone = { w: 200, h: 132 };
  private visible = false;
  private snapped = false;
  private snapT = 0;
  private snapFrom = { x: 0, y: 0, angle: 0 };
  private holdT = 0;
  private listening = false;
  private ready = false;

  constructor(
    private readonly mount: HTMLElement,
    private readonly palette: Palette,
    private readonly onSolved: () => void,
  ) {
    this.root.addChild(this.field, this.receiverGfx, this.playerGfx, this.label);
  }

  async init(): Promise<void> {
    if (this.ready) return;
    let canvas = this.mount.querySelector('canvas');
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.className = 'linkedin-back-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      this.mount.appendChild(canvas);
    }

    const app = new Application();
    await app.init({
      canvas,
      width: this.zone.w,
      height: this.zone.h,
      backgroundAlpha: 0,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      preference: 'webgl',
    });
    app.stage.addChild(this.root);
    this.app = app;
    this.ready = true;
    this.layoutPieces();
    this.drawStatic();
  }

  show(): void {
    void this.init().then(() => {
      this.visible = true;
      this.mount.hidden = false;
      this.holdT = 0;
      this.snapped = false;
      this.snapT = 0;
      this.layoutPieces();
      this.drawStatic();
      this.bindPointer();
    });
  }

  hide(): void {
    this.visible = false;
    this.mount.hidden = true;
    this.unbindPointer();
    this.receiverGfx.clear();
    this.playerGfx.clear();
    this.field.clear();
  }

  resize(w: number, _h: number): void {
    const mobile = w < 720;
    const nw = mobile ? 248 : 200;
    const nh = mobile ? 168 : 132;
    if (this.zone.w !== nw || this.zone.h !== nh) {
      this.zone = { w: nw, h: nh };
      this.app?.renderer.resize(nw, nh);
    }
    if (!this.visible || !this.ready) return;
    this.layoutPieces();
    this.drawStatic();
    this.drawDynamic();
  }

  update(dt: number): void {
    if (!this.visible || !this.ready || !this.body) return;

    if (this.snapped) {
      this.holdT += dt;
      this.updateSnap(dt);
      this.drawDynamic();
      if (this.holdT >= TUNING.snapHold) this.onSolved();
      return;
    }

    if (this.pointer.active) {
      const force = attractionForce(this.body.x, this.body.y, this.pointer.x, this.pointer.y, TUNING);
      const inv = 1 / this.body.mass;
      this.body.vx += force.fx * inv * this.world.fixedDt;
      this.body.vy += force.fy * inv * this.world.fixedDt;
    }

    const align = sampleAlignment(
      this.body.x,
      this.body.y,
      this.body.angle,
      this.receiver.x,
      this.receiver.y,
      this.receiver.angle,
      this.receiver.size,
      TUNING,
    );

    if (align.inSoftField) {
      const soft = softFieldForces(
        this.body.x,
        this.body.y,
        this.body.angle,
        this.receiver.x,
        this.receiver.y,
        this.receiver.angle,
        this.receiver.size,
        TUNING,
      );
      this.body.vx += soft.fx * this.world.fixedDt;
      this.body.vy += soft.fy * this.world.fixedDt;
      this.body.angularVelocity += soft.torque * this.world.fixedDt;
      this.body.vx *= Math.exp(-soft.damp * this.world.fixedDt);
      this.body.vy *= Math.exp(-soft.damp * this.world.fixedDt);
      this.body.angularVelocity *= Math.exp(-soft.damp * this.world.fixedDt);
    }

    if (align.canSnap) this.beginSnap();

    const bounds = { x: 0, y: 0, width: this.zone.w, height: this.zone.h };
    this.world.step(dt, [this.body], bounds);
    this.drawDynamic();
  }

  destroy(): void {
    this.hide();
    this.app?.destroy(true);
    this.app = null;
    this.ready = false;
  }

  private layoutPieces(): void {
    const { w, h } = this.zone;
    const size = w > 220 ? TUNING.squareSize + 6 : TUNING.squareSize;
    const recv = size * TUNING.receiverScale;
    const cx = w * 0.5;

    this.receiver = { x: cx, y: h * 0.38, angle: 0, size: recv };
    this.body = createBody({
      x: cx + (Math.random() - 0.5) * 16,
      y: h * 0.74,
      width: size,
      height: size,
      mass: 1,
    });
    this.body.angle = (Math.random() - 0.5) * 0.35;
    this.body.vx = 0;
    this.body.vy = 0;
    this.label.position.set(10, 8);
  }

  private drawStatic(): void {
    const { w, h } = this.zone;
    this.field.clear();
    this.field.roundRect(0, 0, w, h, 4).fill({ color: this.palette.fieldB, alpha: 1 });
    this.drawDynamic();
  }

  private drawDynamic(): void {
    const r = this.receiver;
    const halfR = r.size * 0.5;

    this.receiverGfx.clear();
    this.receiverGfx
      .rect(r.x - halfR, r.y - halfR, r.size, r.size)
      .fill(this.palette.fieldA);

    const { x, y, angle, width: size } = this.body;
    const half = size * 0.5;
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const corners = [
      [-half, -half],
      [half, -half],
      [half, half],
      [-half, half],
    ];
    const poly: number[] = [];
    for (const [lx, ly] of corners) {
      poly.push(x + lx * c - ly * s, y + lx * s + ly * c);
    }

    this.playerGfx.clear();
    this.playerGfx.poly(poly).fill({ color: this.palette.accent, alpha: this.snapped ? 1 : 0.94 });
  }

  private beginSnap(): void {
    this.snapped = true;
    this.snapT = 0;
    this.holdT = 0;
    this.snapFrom = { x: this.body.x, y: this.body.y, angle: this.body.angle };
    this.body.vx = 0;
    this.body.vy = 0;
    this.body.angularVelocity = 0;
  }

  private updateSnap(dt: number): void {
    this.snapT += dt;
    const t = Math.min(1, this.snapT / TUNING.snapDuration);
    const e = 1 - Math.pow(1 - t, 3);
    const r = this.receiver;
    this.body.x = this.snapFrom.x + (r.x - this.snapFrom.x) * e;
    this.body.y = this.snapFrom.y + (r.y - this.snapFrom.y) * e;
    this.body.angle = this.snapFrom.angle + (r.angle - this.snapFrom.angle) * e;
  }

  private bindPointer(): void {
    if (this.listening) return;
    this.listening = true;
    this.mount.addEventListener('pointerdown', this.onPointerDown, { passive: false });
    this.mount.addEventListener('pointermove', this.onPointerMove, { passive: true });
    this.mount.addEventListener('pointerup', this.onPointerUp);
    this.mount.addEventListener('pointercancel', this.onPointerUp);
    this.mount.addEventListener('pointerleave', this.onPointerLeave);
  }

  private unbindPointer(): void {
    if (!this.listening) return;
    this.listening = false;
    this.mount.removeEventListener('pointerdown', this.onPointerDown);
    this.mount.removeEventListener('pointermove', this.onPointerMove);
    this.mount.removeEventListener('pointerup', this.onPointerUp);
    this.mount.removeEventListener('pointercancel', this.onPointerUp);
    this.mount.removeEventListener('pointerleave', this.onPointerLeave);
    this.pointer.active = false;
  }

  private mapPointer(e: PointerEvent): void {
    const canvas = this.mount.querySelector('canvas');
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * this.zone.w;
    this.pointer.y = ((e.clientY - rect.top) / rect.height) * this.zone.h;
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.visible) return;
    this.mapPointer(e);
    this.pointer.active = true;
    if (e.pointerType === 'touch') e.preventDefault();
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.visible) return;
    this.mapPointer(e);
    if (e.pointerType === 'touch' && e.buttons === 0) return;
    this.pointer.active = true;
  };

  private onPointerUp = (): void => {
    this.pointer.active = false;
  };

  private onPointerLeave = (): void => {
    this.pointer.active = false;
  };
}
